/**
 * Which `/app` paths currently have a gate mounted on them.
 *
 * ## The bug this exists to fix
 *
 * `MEM-019` AC-3: *"A signed-out visitor requesting a gated screen gets the Sign-in required
 * interstitial and returns to the requested screen after signing in."* `ProtectedRoute` renders
 * that interstitial and always has. Nobody ever saw it.
 *
 * `UnauthenticatedRedirector` sits above the router and sends any signed-out visitor on a
 * non-public `/app` path to the login form. It was written as belt-and-braces for a screen
 * somebody forgot to wrap — but it fires for the wrapped ones too, and its effect wins, because
 * an effect that calls `navigate` beats a component that merely rendered. `auth-provider.tsx`
 * asserted the opposite in its own header — *"where `ProtectedRoute` is present it wins by
 * rendering first"* — and that sentence was aspirational rather than true for as long as it
 * existed.
 *
 * The symptom is not subtle once you look: every gated screen in the member app redirected to a
 * bare login form, which is the exact behaviour AC-3 was written to replace. It survived because
 * the end state — a signed-out visitor looking at a sign-in prompt — looks correct in a
 * screenshot, and because the redirect is fast enough that the interstitial never flickers.
 *
 * ## Why a registry rather than a third list of routes
 *
 * The obvious fix is to list the gated paths beside the public ones. That is a third copy of a
 * fact the router already holds, and `public-paths.ts` exists because the *second* copy was
 * already one too many.
 *
 * So the gate registers itself. `ProtectedRoute` claims its path while it is mounted, and the
 * redirector stands down for a claimed path. Effects run child-first, so the claim is in place
 * before the redirector's effect looks — and a route with no gate and no exemption is still
 * caught, which is the safety net the redirector was for.
 */

const claimed = new Set<string>();

/** Called by `ProtectedRoute` while it is mounted. Returns the release. */
export function claimGatedPath(pathname: string): () => void {
  claimed.add(pathname);
  return () => {
    claimed.delete(pathname);
  };
}

/** Whether a gate is mounted on this path and will handle a missing session itself. */
export function isGatedPathClaimed(pathname: string): boolean {
  return claimed.has(pathname);
}

/** Test seam. Nothing in the app calls this. */
export function forgetClaimedPaths(): void {
  claimed.clear();
}
